import { v4 as uuidv4 } from 'uuid';

import { IKnmEvent248 } from 'APIPackage/KnmModule/interfaces/IKnmEvent248';
import { convertFormDateTimeValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateTimeValueToKnmDataDate';
import { IKnmPmFormExtension } from 'KnmPmPackage/PmExtensionModule/interfaces/IKnmPmFormExtension';
import { IKnmEventPeriodExtension } from 'APIPackage/KnmModule/interfaces/IKnmEventPeriodExtension';
import { convertKnmDocumentAttachmentFormValueToKnmData } from 'KnmPackage/MainModule/common/convertKnmDocumentAttachmentFormValueToKnmData';
import { IKnmEventUav } from 'APIPackage/KnmModule/interfaces/IKnmEventUav';

import { IKnmSuspensionEvent248 } from '../../../APIPackage/KnmModule/interfaces/IKnmSuspensionEvent248';
import { convertKnmDocumentAttachmentsFormValuesToKnmData } from '../../../KnmPackage/MainModule/common/convertKnmDocumentAttachmentsFormValuesToKnmData';
import { IKnmErknmFormEvent } from '../interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSuspensionEvent } from '../interfaces/IKnmErknmFormSuspensionEvent';
import { IKnmErknmEventSubmittedDocuments } from '../interfaces/IKnmErknmEventSubmittedDocuments';
import { IKnmEventInspectors } from '../../../APIPackage/KnmModule/interfaces/IKnmEventInspectors';
import { convertEventsProtocolsFormValuesToKnmData } from './convertEventsProtocolsFormValuesToKnmData';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { convertActEventExpertsFormValueToKnmData } from './convertExpertsFormValueToKnmData';

export const convertEventsFormValuesToKnmData = (
  eventsErknm: IKnmErknmFormEvent[] | undefined,
  suspensionEvents?: IKnmErknmFormSuspensionEvent[],
  extension?: IKnmPmFormExtension,
  isDraft?: boolean,
): IKnmEvent248[] | null => {
  if (!eventsErknm) {
    return null;
  }

  const periodExtension: IKnmEventPeriodExtension | null = extension
    ? {
        guid: extension.guid || null,
        attachment: extension.attachment ? convertKnmDocumentAttachmentFormValueToKnmData(extension.attachment) : null,
        dateCreate: convertFormDateTimeValueToKnmDataDate(extension.dateCreate),
        decisionNumber: extension.decisionNumber || null,
        decisionDateTime: convertFormDateTimeValueToKnmDataDate(extension.decisionDateTime),
        amountDays:
          extension.amountDays && !Number.isNaN(Number(extension.amountDays)) ? Number(extension.amountDays) : null,
        isWorkDaysOnly: true,
        initiationDate: convertFormDateTimeValueToKnmDataDate(extension.initiationDate, true) || null,
      }
    : null;

  return eventsErknm.map(item => {
    const formSuspensionEvents: IKnmSuspensionEvent248[] =
      suspensionEvents
        ?.filter(
          suspensionEventItem =>
            item.listId === suspensionEventItem.event?.listId ||
            item.type?.label === suspensionEventItem.event?.type?.label,
        )
        .map(suspensionEventItem => {
          const suspensionEvent: IKnmSuspensionEvent248 = {
            isNew: isDraft ? item.isNew : null,
            guid: suspensionEventItem.guid || null,
            isWorkDaysOnly: suspensionEventItem.isWorkDaysOnly || null,
            attachments: convertKnmDocumentAttachmentsFormValuesToKnmData(suspensionEventItem.attachments),
            decisionDateTime: convertFormDateTimeValueToKnmDataDate(suspensionEventItem.decisionDateTime) || null,
            decisionNumber: suspensionEventItem.decisionNumber || null,
            decisionPlace: suspensionEventItem.decisionPlace || null,
            startDate: convertFormDateTimeValueToKnmDataDate(suspensionEventItem.startDate, true) || null,
            stopDate: convertFormDateTimeValueToKnmDataDate(suspensionEventItem.stopDate, true) || null,
            eventTitle: suspensionEventItem.event?.label || null,
            dateCreate: suspensionEventItem.dateCreate || null,
            manualAddress: suspensionEventItem.manualAddress || null,
            notInFIAS: suspensionEventItem.notInFIAS || null,
            region: suspensionEventItem.region?.id.toString() || null,
            fiasRegion: suspensionEventItem.fiasRegion?.id.toString() || null,
            suspensionDays: suspensionEventItem.suspensionDays || null,
            completed: suspensionEventItem.completed || null,
          };

          return suspensionEvent;
        }) || [];

    const event: IKnmEvent248 = {
      isNew: isDraft ? item.isNew : null,
      guid: item.guid || uuidv4(),
      event: !item.type?.value
        ? null
        : {
            ...item.type?.value,
            inspectionResult: item.type.inspectionResult,
          },
      startDate: convertFormDateTimeValueToKnmDataDate(item.startDate, true) || null,
      stopDate: convertFormDateTimeValueToKnmDataDate(item.stopDate, true) || null,
      dateCreate: item.dateCreate || null,
      suspension: formSuspensionEvents,
      interactionCategory: item.interactionCategory || null,
      inspectors: item.inspectors
        ? item.inspectors.map(x => {
            const eventInspector: IKnmEventInspectors = {
              guid: typeof x.fullName?.id === 'string' ? x.fullName?.id : uuidv4(),
              fullName: x.fullName?.value,
            };

            return eventInspector;
          })
        : null,
      submittedDocuments: !item.submittedDocuments
        ? undefined
        : item.submittedDocuments.map(doc => {
            const document: IKnmErknmEventSubmittedDocuments = {
              ...doc,
              requestDateTime: convertFormDateTimeValueToKnmDataDate(doc.requestDateTime, false) || undefined,
              submissionDateTime: convertFormDateTimeValueToKnmDataDate(doc.submissionDateTime, false) || undefined,
              submissionDeadline: convertFormDateTimeValueToKnmDataDate(doc.submissionDeadline, true) || undefined,
              status: {
                code: doc.status?.code || '',
                id: doc.status?.id || 0,
                title: doc.status?.title || '',
                weight: doc.status?.weight || 0,
              },
            };

            return document;
          }),
      periodExtension: item.type?.title === 'Инструментальное обследование' ? periodExtension : null,
      originalStopDate: convertFormDateTimeValueToKnmDataDate(item.originalStopDate, true) || undefined,
      linkVideo: item.linkVideo || undefined,
      linkVks: item.linkVks || undefined,
      actionDescription: item.actionDescription || undefined,
      actionDescriptionReactQuillEditor: handleReactQuillDeltaValue(
        item.actionDescriptionReactQuillEditor || item.actionDescription,
      ),
      actionNotPerformed: item.actionNotPerformed,
      protocol: convertEventsProtocolsFormValuesToKnmData(item.protocol),
      isFilledFromLink: item.isFilledFromLink || undefined,
      uavTechnologies: item.uavTechnologies?.map(x => {
        const eventUav: IKnmEventUav = {
          guid: x.guid || uuidv4(),
          typeId: x.type?.id || null,
          typeCode: x.type?.name || null,
          name: x.name || null,
        };

        return eventUav;
      }),
      eventCopyNumber: item.eventCopyNumber || null,
      experts: convertActEventExpertsFormValueToKnmData(item.experts),
    };

    return event;
  });
};
